import { Injectable } from '@angular/core';
import { Logger, LoggingService } from './logging.service';

interface StoredTranslationView {
  documentFileId: number | string;
  viewInNativeLanguage: boolean;
}

const STORAGE_KEY = 'translationViewState';

@Injectable({ providedIn: 'root' })
export class TranslationViewStateService {
  static readonly DEFAULT_VIEW_IN_NATIVE_LANGUAGE = true;

  private logger: Logger;

  constructor(private _loggingService: LoggingService) {
    this.logger = this._loggingService.rootLogger.newLogger('TranslationViewStateService');
  }

  get(documentFileId: number | string | null | undefined): boolean {
    const stored = this._read();
    if (documentFileId == null || stored == null || documentFileId !== stored.documentFileId) {
      return TranslationViewStateService.DEFAULT_VIEW_IN_NATIVE_LANGUAGE;
    }
    return stored.viewInNativeLanguage;
  }

  set(documentFileId: number | string | null | undefined, viewInNativeLanguage: boolean): void {
    if (documentFileId == null) {
      return;
    }
    this._write({ documentFileId, viewInNativeLanguage });
  }

  private _read(): StoredTranslationView | null {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const parsed: unknown = JSON.parse(raw);
      return this._isStoredTranslationView(parsed) ? parsed : null;
    } catch (err) {
      this.logger.error('Unable to read the stored translation view state: ' + err.message, { errorObject: err });
      return null;
    }
  }

  private _write(value: StoredTranslationView): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch (err) {
      this.logger.error('Unable to store the translation view state: ' + err.message, { errorObject: err });
    }
  }

  private _isStoredTranslationView(value: unknown): value is StoredTranslationView {
    const candidate = value as Partial<StoredTranslationView> | null;
    return (
      candidate != null &&
      typeof candidate === 'object' &&
      (typeof candidate.documentFileId === 'number' || typeof candidate.documentFileId === 'string') &&
      typeof candidate.viewInNativeLanguage === 'boolean'
    );
  }
}
